import { useEffect, useCallback } from 'react';
import { usePusherContext } from '../context/PusherContext';

interface UsePusherOptions {
  channelName: string;
  eventName: string;
  callback: (data: unknown) => void;
}

/**
 * Hook for subscribing to Pusher events with automatic connection management
 * Connects when the hook mounts, disconnects when it unmounts
 */
export const usePusher = ({
  channelName,
  eventName,
  callback,
}: UsePusherOptions) => {
  const { pusher, connect, disconnect } = usePusherContext();

  // Stable callback ref to avoid re-subscribing on every render
  const stableCallback = useCallback(callback, [callback]);

  useEffect(() => {
    // Request connection when this consumer mounts
    connect();

    return () => {
      // Release connection when this consumer unmounts
      disconnect();
    };
  }, [connect, disconnect]);

  useEffect(() => {
    if (!pusher || !channelName || channelName.includes('undefined')) return;

    const channel = pusher.subscribe(channelName);
    channel.bind(eventName, stableCallback);

    return () => {
      channel.unbind(eventName, stableCallback);
      pusher.unsubscribe(channelName);
    };
  }, [pusher, channelName, eventName, stableCallback]);
};
